<template>
  <main class="record-page">
    <div class="record-shell">
      <template v-if="user">
        <MarketingToolHeader
          eyebrow="Mitarbeiter-Kartei"
          :title="`${user.firstName} ${user.lastName}`"
          description="Lizenzen, Lernfortschritt, Schulungen, Tests und Teacher-Notizen in einer Übersicht."
          icon="mdi-account-school-outline">
          <template v-slot:actions>
            <v-btn
              icon
              class="icon-action academy-local-back"
              aria-label="Zurück zum Teacher Dashboard"
              title="Zurück zum Teacher Dashboard"
              @click="goBack">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-btn outlined color="#58d388" class="secondary-action" @click="exportPdf">
              <v-icon left>mdi-printer-outline</v-icon>Drucken / PDF
            </v-btn>
          </template>
        </MarketingToolHeader>

        <section class="profile-strip" aria-label="Mitarbeiterinformationen">
          <v-avatar size="64" color="#252826"
            ><span>{{ userInitials }}</span></v-avatar
          >
          <div>
            <small>Studio</small><strong>{{ user.studio || "Kein Studio" }}</strong>
          </div>
          <div>
            <small>Rolle</small><strong>{{ user.role || "Member" }}</strong>
          </div>
          <div>
            <small>Aktive Lizenzen</small><strong>{{ activeLicenseCount }}</strong>
          </div>
        </section>

        <div class="record-grid">
          <v-card dark flat class="content-surface">
            <div class="surface-header">
              <span><v-icon>mdi-medal-outline</v-icon></span>
              <div>
                <div class="surface-eyebrow">Qualifikationen</div>
                <h2>Aktive Lizenzen</h2>
              </div>
            </div>
            <div v-if="licenses.length" class="license-list">
              <article
                v-for="license in licenses"
                :key="license._id"
                class="license-item"
                :class="{ blocked: license.status === 'requirements_missing' }"
                :style="{ '--tier-color': tierColor(license.tier) }">
                <span><v-icon>mdi-medal-outline</v-icon></span>
                <div>
                  <strong>{{ license.tier }}</strong
                  ><small>{{ licenseStatusLabel(license) }} · Ablaufdatum {{ formatDate(license.expiresAt) }}</small>
                </div>
                <span v-if="license.status !== 'active'" class="license-warning"><v-icon>mdi-alert-outline</v-icon></span>
              </article>
            </div>
            <div v-else class="inline-empty">Keine Lizenzen vorhanden.</div>
          </v-card>

          <v-card dark flat class="content-surface">
            <div class="surface-header">
              <span><v-icon>mdi-chart-donut</v-icon></span>
              <div>
                <div class="surface-eyebrow">Lernweg</div>
                <h2>Lizenzfortschritt</h2>
              </div>
            </div>
            <div v-if="visibleTierProgress.length" class="tier-progress-list">
              <article v-for="tier in visibleTierProgress" :key="tier.tier" class="tier-progress-item">
                <div class="tier-progress-heading">
                  <span :style="{ '--tier-color': tierColor(tier.tier) }">{{ tierLabel(tier.tier) }}</span>
                  <strong>{{ tier.online.completed }} von {{ tier.online.total }} Modulen · {{ onlinePercent(tier) }}%</strong>
                </div>
                <v-progress-linear
                  :value="onlinePercent(tier)"
                  :color="tierColor(tier.tier)"
                  background-color="rgba(255,255,255,0.08)"
                  height="7"
                  rounded />
                <div class="presence-progress" :class="{ complete: tier.presence.complete }">
                  <v-icon>{{ tier.presence.complete ? "mdi-check-circle-outline" : "mdi-calendar-alert" }}</v-icon>
                  <span v-if="tier.presence.required">
                    Präsenz: {{ tier.presence.completed }} von {{ tier.presence.total }} Pflichtschulungen
                  </span>
                  <span v-else>Keine Präsenzpflicht aktiviert</span>
                </div>
                <div v-if="tier.presence.required && tier.presence.items.length" class="presence-items">
                  <span v-for="item in tier.presence.items" :key="item._id" :class="{ complete: item.completed }">
                    <v-icon>{{ item.completed ? "mdi-check" : "mdi-clock-outline" }}</v-icon>{{ item.title }}
                  </span>
                </div>
              </article>
            </div>
            <div v-else class="inline-empty">Noch keine aktiven Lizenzmodule vorhanden.</div>

            <div v-if="moduleProgressGroups.length" class="module-progress-groups">
              <section v-for="group in moduleProgressGroups" :key="group.tier">
                <h3>{{ tierLabel(group.tier) }}-Module</h3>
                <div class="progress-list">
                  <div v-for="progress in group.items" :key="progress.moduleId" class="progress-item">
                    <div>
                      <strong>{{ progress.moduleTitle }}</strong
                      ><span>{{ progress.percent }}%</span>
                    </div>
                    <v-progress-linear
                      :value="progress.percent"
                      color="#009640"
                      background-color="rgba(255,255,255,0.08)"
                      height="7"
                      rounded />
                  </div>
                </div>
              </section>
            </div>
          </v-card>

          <v-card dark flat class="content-surface">
            <div class="surface-header">
              <span><v-icon>mdi-calendar-check-outline</v-icon></span>
              <div>
                <div class="surface-eyebrow">Aktivitäten</div>
                <h2>Schulungen &amp; Tests</h2>
              </div>
            </div>
            <div class="activity-columns">
              <section>
                <h3>Schulungen</h3>
                <div v-if="registrations.length" class="activity-list">
                  <div v-for="registration in registrations" :key="registration._id">
                    <span>{{ registration.terminTitle }}</span
                    ><strong :class="registration.status === 'attended' ? 'status-success' : ''">{{ registration.status }}</strong>
                  </div>
                </div>
                <div v-else class="inline-empty compact">Keine Schulungen.</div>
              </section>
              <section>
                <h3>Letzte Quizzes</h3>
                <div v-if="quizHistory.length" class="activity-list">
                  <div v-for="quizEntry in quizHistory" :key="quizEntry.date">
                    <span>{{ quizEntry.moduleTitle }}</span
                    ><strong :class="quizEntry.passed ? 'status-success' : 'status-error'">{{ quizEntry.score }}%</strong>
                  </div>
                </div>
                <div v-else class="inline-empty compact">Keine Quizversuche.</div>
              </section>
            </div>
          </v-card>

          <v-card dark flat class="content-surface notes-surface">
            <div class="surface-header">
              <span><v-icon>mdi-note-edit-outline</v-icon></span>
              <div>
                <div class="surface-eyebrow">Interne Begleitung</div>
                <h2>Teacher-Notizen</h2>
              </div>
            </div>
            <v-textarea v-model="newNote" outlined color="#58d388" label="Neue Notiz hinzufügen" rows="3" hide-details class="modern-field" />
            <v-btn color="#009640" depressed class="save-action" :loading="savingNote" :disabled="!newNote.trim()" @click="saveNote">
              <v-icon left>mdi-content-save-outline</v-icon>Speichern
            </v-btn>
            <div v-if="notes.length" class="notes-list">
              <article v-for="note in notes" :key="note._id">
                <div>
                  <strong>{{ note.authorName }}</strong
                  ><time>{{ formatDate(note.createdAt) }}</time>
                </div>
                <p>{{ note.text }}</p>
              </article>
            </div>
            <div v-else class="inline-empty compact notes-empty">Noch keine Notizen.</div>
          </v-card>
        </div>
      </template>

      <div v-else class="loading-surface"><v-progress-circular indeterminate color="#58d388" size="48" width="4" /></div>
    </div>
  </main>
</template>

<script>
import api from "@/api"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"

export default {
  name: "UserKartei",
  components: { MarketingToolHeader },
  data() {
    return {
      user: null,
      licenses: [],
      tierProgress: [],
      progressData: [],
      registrations: [],
      quizHistory: [],
      notes: [],
      newNote: "",
      savingNote: false,
      loading: false,
    }
  },
  computed: {
    activeLicenseCount() {
      return this.licenses.filter((license) => ["active", "expiring_soon"].includes(license.status)).length
    },
    userInitials() {
      if (!this.user) return ""
      return `${this.user.firstName ? this.user.firstName.charAt(0) : ""}${this.user.lastName ? this.user.lastName.charAt(0) : ""}`
    },
    visibleTierProgress() {
      return this.tierProgress.filter((tier) => tier.online.total > 0 || tier.presence.required)
    },
    moduleProgressGroups() {
      return ["bronze", "silber", "gold"]
        .map((tier) => ({ tier, items: this.progressData.filter((progress) => progress.tier === tier) }))
        .filter((group) => group.items.length > 0)
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const record = await api.akademie.fetchUserKartei(this.$route.params.id)
        if (record) {
          this.user = record.user
          this.notes = record.notes || []
          this.licenses = record.licenses || []
          this.tierProgress = record.tierProgress || []
          this.progressData = record.progressData || []
          this.registrations = record.registrations || []
          this.quizHistory = record.quizHistory || []
        }
      } catch (error) {
        console.error("Fehler beim Laden der Kartei:", error)
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      return date ? new Date(date).toLocaleDateString("de-CH") : ""
    },
    tierColor(tier) {
      const colors = { bronze: "#d89557", silber: "#d4d4d8", gold: "#f4c95d" }
      return colors[(tier || "").toLowerCase()] || "#58d388"
    },
    tierLabel(tier) {
      const labels = { bronze: "Bronze", silber: "Silber", gold: "Gold" }
      return labels[(tier || "").toLowerCase()] || "Lizenzstufe"
    },
    onlinePercent(tier) {
      const total = Number(tier && tier.online && tier.online.total) || 0
      const completed = Number(tier && tier.online && tier.online.completed) || 0
      return total ? Math.round((completed / total) * 100) : 0
    },
    licenseStatusLabel(license) {
      const labels = {
        active: "Aktiv",
        expiring_soon: "Läuft bald ab",
        expired: "Abgelaufen",
        requirements_missing: "Pflichtanforderung offen",
      }
      return labels[license && license.status] || "Unbekannter Status"
    },
    async saveNote() {
      if (!this.newNote.trim()) return
      this.savingNote = true
      try {
        await api.akademie.addTeacherNote(this.user._id, this.newNote)
        this.newNote = ""
        await this.loadData()
      } catch (error) {
        console.error("Fehler beim Speichern der Notiz:", error)
      } finally {
        this.savingNote = false
      }
    },
    goBack() {
      if (window.history.length > 1) this.$router.back()
      else this.$router.push("/akademie-teacher")
    },
    exportPdf() {
      window.print()
    },
  },
}
</script>

<style scoped>
.record-page {
  --surface: rgba(255, 255, 255, 0.035);
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.record-shell {
  width: 100%;
  max-width: 78rem;
  margin: 0 auto;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #fff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}
.secondary-action,
.save-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}
.save-action {
  margin-top: 0.75rem;
  color: #fff !important;
}

.profile-strip {
  display: grid;
  grid-template-columns: auto repeat(3, minmax(0, 1fr));
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem;
  background: var(--surface);
}
.profile-strip .v-avatar span {
  color: #7ee5a6;
  font-size: 1.25rem;
  font-weight: 800;
}
.profile-strip > div {
  display: grid;
  min-width: 0;
  padding-left: 1rem;
  border-left: 1px solid rgba(255, 255, 255, 0.075);
}
.profile-strip small {
  color: var(--muted);
  font-size: 0.75rem;
}
.profile-strip strong {
  overflow: hidden;
  color: #fff;
  font-size: 0.9375rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
.record-grid > * {
  min-width: 0;
}
.content-surface {
  min-width: 0;
  min-height: 20rem;
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
}
.surface-header {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.surface-header > div {
  min-width: 0;
}
.surface-header > span {
  display: grid;
  width: 2.625rem;
  height: 2.625rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}
.surface-header > span .v-icon {
  color: currentColor;
}
.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.surface-header h2 {
  margin: 0.1875rem 0 0;
  color: #fff;
  font-size: 1.25rem;
}

.license-list,
.progress-list,
.activity-list,
.notes-list {
  display: grid;
  gap: 0.5rem;
}
.license-item {
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-left: 3px solid var(--tier-color);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.12);
}
.license-item.blocked {
  border-color: rgba(255, 141, 152, 0.25);
  opacity: 0.82;
}
.license-item > span:first-child {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  color: var(--tier-color);
  background: rgba(255, 255, 255, 0.055);
}
.license-item > span .v-icon {
  color: currentColor;
}
.license-item > div {
  display: grid;
}
.license-item strong {
  color: #fff;
  font-size: 0.875rem;
  text-transform: capitalize;
}
.license-item small {
  color: var(--muted);
  font-size: 0.75rem;
}
.license-warning {
  color: #fcd34d;
}

.progress-item > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
}
.progress-item strong {
  min-width: 0;
  color: #d4d4d8;
  font-size: 0.875rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.progress-item span {
  flex: 0 0 auto;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 750;
}

.tier-progress-list {
  display: grid;
  gap: 0.625rem;
}

.tier-progress-item {
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.12);
}

.tier-progress-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.625rem;
  margin-bottom: 0.5rem;
}

.tier-progress-heading > span {
  color: var(--tier-color);
  font-size: 0.875rem;
  font-weight: 800;
}

.tier-progress-heading strong {
  color: #d4d4d8;
  font-size: 0.75rem;
  text-align: right;
}

.presence-progress {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.5rem;
  color: #fbbf24;
  font-size: 0.75rem;
  line-height: 1.4;
}

.presence-progress.complete {
  color: #7ee5a6;
}

.presence-progress .v-icon {
  flex: 0 0 auto;
  color: currentColor;
  font-size: 1rem;
}

.presence-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.625rem;
}

.presence-items > span {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid rgba(251, 191, 36, 0.2);
  border-radius: 999px;
  color: #fcd34d;
  background: rgba(251, 191, 36, 0.08);
  font-size: 0.75rem;
  overflow-wrap: anywhere;
}

.presence-items > span.complete {
  border-color: rgba(88, 211, 136, 0.2);
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.08);
}

.presence-items .v-icon {
  flex: 0 0 auto;
  color: currentColor;
  font-size: 0.875rem;
}

.module-progress-groups {
  display: grid;
  gap: 0.875rem;
  margin-top: 1.125rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
}

.module-progress-groups section {
  min-width: 0;
}

.module-progress-groups h3 {
  margin: 0 0 0.625rem;
  color: #ffffff;
  font-size: 0.875rem;
}

.activity-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-width: 0;
  gap: 0.75rem;
}
.activity-columns section {
  min-width: 0;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.12);
}
.activity-columns h3 {
  margin: 0 0 0.625rem;
  color: #fff;
  font-size: 0.875rem;
}
.activity-list > div {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: flex-start;
  min-width: 0;
  gap: 0.75rem;
  color: #c7c7cb;
  font-size: 0.875rem;
}
.activity-list > div span {
  min-width: 0;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.activity-list strong {
  flex: 0 0 auto;
  color: #a3a3a8;
  font-size: 0.75rem;
  line-height: 1.4;
  text-align: right;
}
.status-success {
  color: #7ee5a6 !important;
}
.status-error {
  color: #ff8d98 !important;
}

.modern-field ::v-deep .v-input__slot {
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}
.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}
.modern-field ::v-deep .v-label {
  font-size: 0.875rem;
}
.notes-list {
  max-height: 14rem;
  margin-top: 1rem;
  overflow-y: auto;
}
.notes-list article {
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.12);
}
.notes-list article > div {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}
.notes-list strong,
.notes-list time {
  color: var(--muted);
  font-size: 0.75rem;
}
.notes-list p {
  margin: 0.375rem 0 0;
  color: #d4d4d8;
  font-size: 0.875rem;
  line-height: 1.5;
}
.inline-empty {
  display: grid;
  min-height: 8rem;
  place-items: center;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 0.875rem;
  color: var(--muted);
  font-size: 0.875rem;
  text-align: center;
}
.inline-empty.compact {
  min-height: 4rem;
}
.notes-empty {
  margin-top: 1rem;
}
.loading-surface {
  display: grid;
  min-height: 70vh;
  place-items: center;
}

@media (max-width: 59.9375rem) {
  .record-page {
    padding: 1.125rem;
  }
  .record-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 37.4375rem) {
  .record-page {
    padding: 0.75rem;
  }
  .profile-strip {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.75rem;
  }
  .profile-strip > div {
    padding: 0.625rem 0 0;
    border-top: 1px solid rgba(255, 255, 255, 0.075);
    border-left: 0;
  }
  .profile-strip > div:nth-of-type(1) {
    border-top: 0;
  }
  .content-surface {
    min-height: auto;
    padding: 0.875rem;
    border-radius: 1.0625rem !important;
  }
  .activity-columns {
    grid-template-columns: minmax(0, 1fr);
  }
  .tier-progress-heading {
    align-items: flex-start;
    flex-direction: column;
  }
  .tier-progress-heading strong {
    text-align: left;
  }
}

@media print {
  .record-page {
    padding: 0;
    color: #000;
    background: #fff;
  }
  .record-page ::v-deep .marketing-tool-header {
    border-color: #ccc;
    background: #fff;
    box-shadow: none;
  }
  .record-page ::v-deep .header-copy h1,
  .record-page ::v-deep .header-copy p {
    color: #000;
  }
  .record-page ::v-deep .header-actions {
    display: none;
  }
  .profile-strip,
  .content-surface {
    break-inside: avoid;
    border-color: #ccc;
    color: #000;
    background: #fff !important;
    box-shadow: none;
  }
  .surface-header h2,
  .profile-strip strong,
  .license-item strong,
  .activity-columns h3 {
    color: #000;
  }
  .save-action,
  .modern-field {
    display: none;
  }
}
</style>
